import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { endpoints, urlAgentsDashboardAskJob } from "../config/api";
import { getAuthHeaders } from "../utils/getAuthHeaders";
import { resolveCurrentUserIndex } from "../utils/getTokenPayload";
import { getErrorMessage } from "../utils/getErrorMessage";
import { fetchAllDashboardsAdmin } from "../utils/fetchDashboardsAdmin";
import MultiSelectDropdown from "./MultiSelectDropdown";
import DashboardAskJobResponse from "./DashboardAskJobResponse";
import DashboardSummariserApiDocs from "./DashboardSummariserApiDocs";
import {
  GuideCloseButton,
  titleStyle,
  calloutStyle,
  introStyle,
} from "./dashboardSkillGuideCommon";

const DEFAULT_QUESTION = "Summarize the key takeaways from this dashboard.";
const POLL_INTERVAL_MS = 3000;

const labelStyle = {
  display: "block",
  fontSize: "13px",
  fontWeight: 600,
  color: "#3730a3",
  marginBottom: "8px",
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 12px",
  borderRadius: "6px",
  border: "1px solid #d1d5db",
  fontSize: "14px",
  color: "#1e293b",
};

const textareaStyle = {
  ...inputStyle,
  minHeight: "100px",
  resize: "vertical",
  fontFamily: "inherit",
  lineHeight: 1.5,
};

const formSectionStyle = {
  marginBottom: "24px",
};

const submitButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: "42px",
  padding: "0 20px",
  borderRadius: "6px",
  border: "none",
  backgroundColor: "#3730a3",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "14px",
  cursor: "pointer",
};

const submitButtonDisabledStyle = {
  ...submitButtonStyle,
  opacity: 0.6,
  cursor: "not-allowed",
};

const errorBoxStyle = {
  padding: "12px 16px",
  borderRadius: "8px",
  backgroundColor: "#fee2e2",
  border: "1px solid #fca5a5",
  color: "#b91c1c",
  fontSize: "14px",
  marginBottom: "16px",
};

const footerNoteStyle = {
  marginTop: "32px",
  padding: "16px 20px",
  borderRadius: "8px",
  backgroundColor: "#f1f5f9",
  border: "1px solid #cbd5e1",
  color: "#475569",
  fontSize: "14px",
  lineHeight: 1.6,
};

const importantNoteStyle = {
  backgroundColor: "#eff6ff",
  border: "1px solid #3b82f6",
  borderRadius: "8px",
  padding: "16px 20px",
  marginBottom: "16px",
  color: "#1e40af",
  fontSize: "14px",
  lineHeight: 1.6,
};

const buildDashboardOptions = (dashboards) =>
  dashboards
    .filter((dashboard) => dashboard?.id != null)
    .map((dashboard) => ({
      value: String(dashboard.id),
      label: `${dashboard.title || "Untitled dashboard"} (id ${dashboard.id})`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

const DashboardSummariser = ({ onClose }) => {
  const [dashboards, setDashboards] = useState([]);
  const [loadingDashboards, setLoadingDashboards] = useState(true);
  const [dashboardError, setDashboardError] = useState(null);
  const [selectedDashboardIds, setSelectedDashboardIds] = useState([]);
  const [question, setQuestion] = useState(DEFAULT_QUESTION);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [jobResponse, setJobResponse] = useState(null);
  const [pollError, setPollError] = useState(null);
  const [polling, setPolling] = useState(false);
  const pollTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const loadDashboards = async () => {
      setLoadingDashboards(true);
      setDashboardError(null);
      try {
        const all = await fetchAllDashboardsAdmin();
        if (!cancelled) setDashboards(all);
      } catch (err) {
        console.error("Error loading dashboards for summariser", err);
        if (!cancelled) {
          setDashboards([]);
          setDashboardError(getErrorMessage(err, "Failed to load dashboards"));
        }
      } finally {
        if (!cancelled) setLoadingDashboards(false);
      }
    };

    loadDashboards();
    return () => {
      cancelled = true;
    };
  }, []);

  const dashboardOptions = useMemo(() => buildDashboardOptions(dashboards), [dashboards]);

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const pollJob = useCallback(
    async (activeJobId) => {
      if (!activeJobId) return null;

      setPolling(true);
      setPollError(null);

      try {
        const response = await axios.get(urlAgentsDashboardAskJob(activeJobId), {
          headers: {
            ...getAuthHeaders(),
            Accept: "*/*",
          },
        });
        const body = response.data || {};
        setJobResponse(body);

        const status = String(body.status || "").toLowerCase();
        if (status === "completed" || status === "failed") {
          clearPollTimer();
        }

        return body;
      } catch (err) {
        console.error("Error polling dashboard ask job", err);
        setPollError(getErrorMessage(err, "Failed to poll job"));
        clearPollTimer();
        return null;
      } finally {
        setPolling(false);
      }
    },
    [clearPollTimer]
  );

  useEffect(() => {
    clearPollTimer();

    if (!jobId) return undefined;

    pollJob(jobId);
    pollTimerRef.current = setInterval(() => {
      pollJob(jobId);
    }, POLL_INTERVAL_MS);

    return () => {
      clearPollTimer();
    };
  }, [jobId, pollJob, clearPollTimer]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError(null);

    const dashboardIds = selectedDashboardIds
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id));

    if (dashboardIds.length === 0) {
      setSubmitError("Select at least one dashboard.");
      return;
    }

    const trimmedQuestion = question.trim();
    if (!trimmedQuestion) {
      setSubmitError("Enter a question.");
      return;
    }

    setSubmitting(true);
    setJobId(null);
    setJobResponse(null);
    setPollError(null);
    clearPollTimer();

    let userIndex;
    try {
      userIndex = await resolveCurrentUserIndex();
    } catch (err) {
      console.error("Error resolving user index", err);
      setSubmitError(getErrorMessage(err, "Failed to resolve your user index"));
      setSubmitting(false);
      return;
    }

    if (userIndex == null) {
      setSubmitError(
        "Could not resolve your user index from the session. Log out and log in again, or contact support if the issue persists."
      );
      setSubmitting(false);
      return;
    }

    const payload = {
      dashboardIds,
      question: trimmedQuestion,
      user_index: userIndex,
    };

    try {
      const response = await axios.post(endpoints.agentsDashboardAsk, payload, {
        headers: {
          ...getAuthHeaders(),
          Accept: "*/*",
          "Content-Type": "application/json",
        },
      });

      const nextJobId = response.data?.jobId;
      if (!nextJobId) {
        throw new Error("Job id missing in response");
      }

      setJobId(nextJobId);
      setJobResponse({
        jobId: nextJobId,
        status: response.data?.status || "pending",
        answer: null,
        summary: null,
        conclusion: null,
        rawAnswer: null,
      });
    } catch (err) {
      console.error("Error submitting dashboard ask", err);
      setSubmitError(getErrorMessage(err, "Failed to submit question"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <GuideCloseButton onClose={onClose} />

      <h2 style={titleStyle}>Dashboard Summariser</h2>
      <p style={introStyle}>
        Ask the dashboard agent to summarize one or more dashboards. The request runs asynchronously;
        poll the job for the answer.
      </p>

      <div style={importantNoteStyle} role="note">
        <strong>Important note:</strong> This will only work best when sections and keywords exist on a
        dashboard. If they are missing, go to Manage Dashboards, add them, cross-check, then use this
        feature.
      </div>

      <div style={calloutStyle} role="note">
        <strong>Testing &amp; development only.</strong> This feature currently works on the dev branch
        deployment only. If it is not working, contact the backend team to cross-check the deployment.
      </div>

      {(dashboardError || submitError) && (
        <div style={errorBoxStyle}>{submitError || dashboardError}</div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={formSectionStyle}>
          <label style={labelStyle} htmlFor="dashboard-summariser-dashboards">
            Dashboards
          </label>
          <MultiSelectDropdown
            options={dashboardOptions}
            value={selectedDashboardIds}
            onChange={setSelectedDashboardIds}
            placeholder={loadingDashboards ? "Loading dashboards…" : "Search and select dashboards…"}
            searchable
            disabled={loadingDashboards || submitting}
            aria-label="Select dashboards"
          />
        </div>

        <div style={formSectionStyle}>
          <label style={labelStyle} htmlFor="dashboard-summariser-question">
            Question
          </label>
          <textarea
            id="dashboard-summariser-question"
            style={textareaStyle}
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            disabled={submitting}
            required
          />
        </div>

        <button
          type="submit"
          style={submitting ? submitButtonDisabledStyle : submitButtonStyle}
          disabled={submitting || loadingDashboards}
        >
          {submitting ? "Submitting…" : "Ask dashboard agent"}
        </button>
      </form>

      <DashboardAskJobResponse
        jobId={jobId}
        response={jobResponse}
        loading={polling}
        error={pollError}
        onPoll={() => pollJob(jobId)}
        polling={polling}
      />

      <DashboardSummariserApiDocs />

      <div style={footerNoteStyle} role="note">
        <strong>Future scope:</strong> Need to work on whether an auto email send cron job is required
        for this feature.
      </div>
    </div>
  );
};

export default DashboardSummariser;
